import container from "@/lib/di/container";
import { useEffect, useState } from "react";
import { FilterBar, FilterInputTemplate, FilterInputType } from "../../components/filter/FilterBar";
import { FilterSearchPayload, DataTable } from "../../components/table/DataTable";
import { DetailModal } from "./modals/DetailModal";
import { Button, message, Popconfirm, Tooltip } from "antd";
import { InfoCircleOutlined, LockOutlined, UnlockOutlined } from "@ant-design/icons";
import { ReadMeterPeriodModel, ReadMeterPeriodModelFactory } from "@/core/models/read-meter-period.model";
import { ReadMeterPeriodRepo } from "@/core/repos/tables/read-meter-period.repo";
import { formatDateTimePipe } from "@/core/pipes/format-date.pipe";
import { GlobalUtils } from "../../utils/global.utils";
import { useTitleDataStore } from "@/core/stores/title-data.store";
import { DeleteButton } from "../../components/table/DeleteButton";

const readMeterPeriodRepo = container.get(ReadMeterPeriodRepo);

export default function ReadMeterPeriodPage() {
    const titleData = useTitleDataStore();
    const [modalData, setModalData] = useState<ReadMeterPeriodModel>(ReadMeterPeriodModelFactory.getEmpty())
    const [filterMap, setFilterMap] = useState<Map<string, FilterSearchPayload>>(new Map(
        Object.entries({...GlobalUtils.getPreviousMonthFilter()})
    ))
    const [selectedRows, setSelectedRows] = useState<ReadMeterPeriodModel[]>([]);
    const [isDetailModal, setDetailModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [locking, setLocking] = useState(false);
    const [unlocking, setUnlocking] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [filterTemplates] = useState<FilterInputTemplate[]>([
        {
            dataIndex: "search",
            label: "Tìm kiếm",
            placeholder: "Nhập nội dung tìm kiếm",
            type: FilterInputType.text,
        },
        {
            dataIndex: "month",
            label: "Tháng",
            placeholder: "Chọn tháng",
            type: FilterInputType.month,
        },
    ])

    const init = async () => {
        titleData.setTitleActions({
            text: "Thêm lịch đọc số nước",
            onPress: () => {
                showDetail(ReadMeterPeriodModelFactory.getEmpty())
            }
        })
    }

    useEffect(()=>{init()}, []);

    const showDetail = (record: ReadMeterPeriodModel) => {
        setModalData(record);
        setDetailModal(true);
    };

    const deleteSelected = async () => {
        setDeleting(true);
        const result = await readMeterPeriodRepo.deleteMany(selectedRows);
        messageApi.success({
            content: `Bạn đã xoá thành công ${result.success}/${result.total} bản ghi`
        })
        setDeleting(false);
    }

    const toggleLock = async (isAllowReading: boolean) => {
        if (isAllowReading) {
            setUnlocking(true);
        } else {
            setLocking(true);
        }
        const month = parseInt(filterMap.get("month")?.value as string??"0");
        const year = parseInt(filterMap.get("year")?.value as string??"0");
        await readMeterPeriodRepo.toggleLock({
            isAllowReading,
            month,
            year
        })
        messageApi.success({
            content: `Bạn đã ${isAllowReading?"khóa":"mở khóa"} thành công lịch đọc số nước tháng ${month}/${year}`
        })
        setLocking(false);
        setUnlocking(false);
    }


    async function onFilterChange(map: Map<string, FilterSearchPayload>) {
        setFilterMap(new Map(map));
    }

    function onRowSelect(rows: ReadMeterPeriodModel[]) {
        setSelectedRows([...rows]);
    }

    return (
    <div className="px-4 py-6">
        {contextHolder}
        <FilterBar setMap={setFilterMap} onChange={onFilterChange} templates={filterTemplates} map={filterMap} actions={[
            <div key={1} className="mt-6">
                {/* <Button type="primary" icon={<FileExcelOutlined />} style={{ background: "#29A339"}}>Xuất Excel</Button> */}
                <DeleteButton loading={deleting} length={selectedRows.length} onConfirm={deleteSelected} noGap/>
            </div>,
            <div key={2} className="col-span-4">
                <Popconfirm
                    description={`Bạn có muốn khóa TOÀN BỘ lịch đọc số nước tháng ${filterMap.get("month")?.value??0}/${filterMap.get("year")?.value??2000}?`}
                    title={`Bạn có chắc chắn muốn khóa lịch đọc số nước?`}
                    onConfirm={() => toggleLock(false)}
                    okText="Khóa"
                    cancelText="Huỷ"
                >
                    <Button
                        style={{ background: "var(--primary-button)"}}
                        loading={locking} type="primary" icon={<LockOutlined />} 
                        disabled={filterMap.get("month") === undefined||filterMap.get("year") === undefined}>Khóa Lịch đọc số nước</Button>
                </Popconfirm>
                <Popconfirm
                    description={`Bạn có muốn mở khóa TOÀN BỘ lịch đọc số nước tháng ${filterMap.get("month")?.value??0}/${filterMap.get("year")?.value??2000}?`}
                    title={`Bạn có chắc chắn muốn mở khóa lịch đọc số nước?`}
                    onConfirm={() => toggleLock(true)}
                    okText="Mở khóa"
                    cancelText="Huỷ"
                >
                    <Button className='ml-4'
                        style={{ background: "var(--primary-button)"}}
                        loading={unlocking} type="primary" icon={<UnlockOutlined />} 
                        disabled={filterMap.get("month") === undefined||filterMap.get("year") === undefined}>Mở khóa Lịch đọc số nước</Button>
                </Popconfirm>
            </div>,

        ]}/>
        <div className="mt-4 border-t border-dashed border-gray-300 pt-6">
            <DataTable<ReadMeterPeriodModel>
                columns={[
                    { name: 'Khu vực', pipe: (value) => value.tollArea.name },
                    { dataIndex: 'fromDate', name: 'Từ ngày', pipe: formatDateTimePipe },
                    { dataIndex: 'toDate', name: 'Đến ngày', pipe: formatDateTimePipe },
                    { name: 'Tiến độ đọc số', pipe: (value) => `${value.waterIndexTotal}/${value.waterUserTotal}` },
                    { dataIndex: 'lockTime', name: 'Thời gian khóa', pipe: formatDateTimePipe },
                    { dataIndex: 'isAllowReading', name: 'Cho phép đọc', pipe: (value) => value?"Cho phép":"Không cho phép"},
                    {
                        name: 'Thao tác',
                        render: (_, record) => (
                            <div className="flex flex-row px-1 items-center justify-center">
                                <Tooltip title="Sửa lịch đọc số nước"><Button type="text" icon={<InfoCircleOutlined />} onClick={() => showDetail(record)}/></Tooltip>
                            </div>
                        ),
                        fixed: "right",
                    },
                ]}
                repository={readMeterPeriodRepo}
                filterMap={filterMap}
                onSelect={onRowSelect}
            ></DataTable>
        </div>
        <DetailModal isModalOpen={isDetailModal} setIsModalOpen={setDetailModal} data={modalData}></DetailModal>
    </div>)
}